import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import { AxiosError } from 'axios';
import api, { getCsrfCookie } from '@/lib/api';

export interface Partner {
  uuid: string;
  name: string;
  email: string;
  role: 'admin' | 'support';
  isActive: boolean;
  lastLoginAt: string | null;
}

interface PartnerAuthState {
  partner: Partner | null;
  isAuthenticated: boolean;
  /**
   * True while fetchMe() verifies the session against the server. Starts true so
   * the first render cannot act on a role rehydrated from localStorage: zustand
   * hydrates synchronously, so `partner` is populated before any effect runs.
   * Only ProtectedRoute should read this.
   */
  isLoading: boolean;
  /**
   * True only while a login request is in flight. Deliberately separate from
   * isLoading: /login renders outside ProtectedRoute and never calls fetchMe(),
   * so a login button bound to isLoading would sit disabled forever.
   */
  isLoggingIn: boolean;
  login: (email: string, password: string) => Promise<void>;
  logout: () => Promise<void>;
  fetchMe: () => Promise<void>;
  setPartner: (partner: Partner) => void;
}

export function getApiErrorMessage(error: unknown): string {
  if (error instanceof AxiosError) {
    const data = error.response?.data as { message?: string; errors?: Record<string, string[]> } | undefined;
    if (data?.message) return data.message;
    if (data?.errors) {
      const first = Object.values(data.errors)[0];
      if (first?.[0]) return first[0];
    }
  }
  return 'Something went wrong. Please try again.';
}

export const usePartnerAuthStore = create<PartnerAuthState>()(
  persist(
    (set) => ({
      partner: null,
      isAuthenticated: false,
      // Starts true, not false. zustand/persist hydrates `partner` and
      // `isAuthenticated` from localStorage synchronously, before this
      // module's consumers ever render, but `isLoading` is excluded from
      // partialize and always starts fresh. If it started false, the very
      // first render would treat a stale rehydrated role as settled and
      // ProtectedRoute/RequireAdmin would render children before fetchMe()
      // ever ran, letting a self-demoted admin's cached role grant access
      // for one paint. Starting true forces every mount to show the
      // skeleton until fetchMe() resolves at least once. fetchMe() sets
      // isLoading: false on both its success and failure paths, so this
      // cannot hang. Do not "tidy" this back to false.
      isLoading: true,
      isLoggingIn: false,

      login: async (email: string, password: string) => {
        set({ isLoggingIn: true });
        try {
          await getCsrfCookie();
          const { data } = await api.post('/login', { email, password });
          set({ partner: data.data, isAuthenticated: true, isLoggingIn: false });
        } catch (error) {
          set({ isLoggingIn: false });
          throw error;
        }
      },

      logout: async () => {
        try {
          await api.post('/logout');
        } finally {
          set({ partner: null, isAuthenticated: false });
        }
      },

      fetchMe: async () => {
        set({ isLoading: true });
        try {
          const { data } = await api.get('/me');
          set({ partner: data.data, isAuthenticated: true, isLoading: false });
        } catch {
          set({ partner: null, isAuthenticated: false, isLoading: false });
        }
      },

      setPartner: (partner: Partner) => set({ partner }),
    }),
    {
      name: 'partner-auth-storage',
      partialize: (state) => ({ partner: state.partner, isAuthenticated: state.isAuthenticated }),
    },
  ),
);
